import { Box, Button, Divider, Paper, Slide, Tooltip, Typography } from "@mui/material";
import { BlockDateModel } from "Backend";
import ErrorIcon from "@mui/icons-material/ErrorOutline";

import { CardBanner } from "./HitCard/CardBanner";
import Scroll from "react-perfect-scrollbar";
import { SimpleMenuItem } from "components/SimpleMenu";
import { useFormCard } from "./useFormCard";
import Flex from "components/Flex";
import DocumentName from "components/DocumentName";
import { TimelineIcon } from "components/Icons";
import SaveSpinner from "components/SaveSpinner";
import CardFormFragment from "./HitCard/CardFormFragment";
import SaveIcon from "@mui/icons-material/Save";
import { Block, DocumentIntake } from "generated/graphql";
import theme from "theme";

export interface BadReadCardProps {
  lensId: number;
  caseId: number;
  intake: DocumentIntake | undefined;
  blocks: Block[];
  onDocumentClick: () => void;
  dismissPage: () => void;
}

export const BadReadCard = (props: BadReadCardProps) => {
  const { blocks, lensId, caseId, intake } = props;

  const first = blocks[0];

  if (first === undefined) return <></>;

  const date = first.date && new BlockDateModel(first.date);

  const docName = intake?.name;

  const actionItems: () => SimpleMenuItem[] | undefined = () => {
    return undefined;
  };

  const handlePageDone = () => {
    props.dismissPage();
  };

  const formCard = useFormCard({
    lensId,
    caseId,
    intakeId: first.documentIntakeId,
    intakePage: first.originalPageNumber,
    blockIndex: first.index,
  });
  const {
    formModeState: [formMode, setFormMode],
    form,
    serverValues,
    savingFormState: [savingForm],
    saveFormValue,
    toggleFormMode,
    canSave,
  } = formCard;

  const addToProfileButton = (
    <Tooltip arrow title={formMode ? `Save` : `Pin to Profile`}>
      <Button
        size="small"
        variant="outlined"
        sx={{
          marginLeft: theme.spacing(1),
        }}
        color="default"
        disabled={formMode ? !canSave : false}
        startIcon={<TimelineIcon size={16} />}
        onClick={toggleFormMode}>
        {formMode ? (savingForm ? "Saving" : "Save") : `Pin to Profile`}
        {savingForm && <SaveSpinner style={{ marginLeft: 4 }} state="saving" />}
      </Button>
    </Tooltip>
  );

  const details = (
    <Flex flexGrow={1} p={1}>
      <Flex flexDirection="column" justifyContent="space-between">
        <Flex flexDirection="row" justifyContent="flex-end">
          <DocumentName name={docName || ""} onClick={() => props.onDocumentClick()} />
        </Flex>
      </Flex>
      <Flex
        component={Scroll}
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: theme.spacing(1),
          fontSize: "11pt",
          minHeight: "150px",
          maxHeight: "250px",
          borderColor: "black",
          borderStyle: "dashed",
          borderWidth: "1px",
        }}>
        {blocks.map((block) => (
          <Typography key={block.index}>{block.text}</Typography>
        ))}
      </Flex>
      {formMode && <Divider />}
      {formMode && (
        <Slide in direction="up">
          <Box display="flex" flexGrow={1} flexDirection="column" justifyContent="space-between">
            {form && serverValues && (
              <CardFormFragment
                useFormCard={formCard}
                docPageCount={intake?.pages || 1}
                block={first}
                showReferenceType={true}
                showHighlightedText={false}
              />
            )}
            <Flex flexDirection="row" justifyContent="flex-end" p={1}>
              <Button variant="outlined" onClick={() => setFormMode(false)}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="default"
                disabled={!canSave}
                onClick={() => saveFormValue()}
                endIcon={<SaveIcon />}>
                Pin to Profile
              </Button>
            </Flex>
          </Box>
        </Slide>
      )}
      <Divider />
      <Box display="flex" justifyContent="flex-end">
        <Box p={0.5}>
          {!formMode && (
            <Button variant="contained" color="default" onClick={handlePageDone}>
              Page Done
            </Button>
          )}
        </Box>
      </Box>
    </Flex>
  );

  return (
    <Flex component={Paper} width="100%" height="500px">
      <Scroll style={{ display: "flex", flexDirection: "column" }}>
        <CardBanner
          icon={<ErrorIcon />}
          titleText="Unreadable Text"
          date={date}
          actionItems={actionItems()}
          addEventButton={form && addToProfileButton}
        />
        <Divider />
        {details}
      </Scroll>
    </Flex>
  );
};

export default BadReadCard;
